import axios from "axios";
import { apiUrl } from "../constant";
import { appendFilesToUploadData } from "../config/utils";
/* istanbul ignore next */
const API_BASE = `${apiUrl}/api/CarrierConnect`;

// Helper function to get package tracking number with fallbacks
const getPackageTrackingNumber = (pkg) => {
    return safeString(pkg.carrierConnectPackageNumber) || safeString(pkg.trackingNumber);
};

// Helper function to get package type with fallbacks
const getPackageType = (pkg) => {
    return safeString(pkg.pkgType) || safeString(pkg.packageType);
};

// Helper function to build package details for data-only service
/* istanbul ignore next */
const buildPackageDetails = (packages) => {
    return (packages || []).map(pkg => ({
        carrierConnectPackageNumber: getPackageTrackingNumber(pkg),
        pkgType: getPackageType(pkg),
        length: safeParseFloat(pkg.length),
        width: safeParseFloat(pkg.width),
        height: safeParseFloat(pkg.height),
        weight: safeParseFloat(pkg.weight),
        dimensionUnitId: safeParseInt(pkg.dimensionUnitId),
        weightUnitId: safeParseInt(pkg.weightUnitId),
        packageCount: safeParseInt(pkg.packageCount),
        carrierCompany_Id: safeParseInt(pkg.carrierCompanyId),
        service_Id: safeParseInt(pkg.serviceId),
        package_Id: safeParseInt(pkg.packageId),
        trackingNumber: safeString(pkg.trackingNumber),
        notes: safeString(pkg.notes)
    }));
};

// Helper function to build carrier connect payload for data-only service
/* istanbul ignore next */
const buildCarrierConnectPayload = (data) => {
    return {
        carrierConnectNumber: safeString(data.carrierConnectId),
        carrierId: safeParseInt(data.carrierId),
        statusId: safeParseInt(data.carrierStatus),
        trackingNumber: safeString(data.trackingNumber),
        subTrackingNumber: safeString(data.subTrackingNumber),
        length: safeParseFloat(data.length),
        width: safeParseFloat(data.width),
        height: safeParseFloat(data.height),
        weight: safeParseFloat(data.weight),
        dimensionUnitId: safeParseInt(data.dimensionUnitId),
        weightUnitId: safeParseInt(data.weightUnitId),
        shippedDate: data.shippedDate || null,
        notes: safeString(data.notes),
        packageCount: safeParseInt(data.packageCount),
        tenantId: safeParseInt(data.tenantId),
        carrierConnectCompanyId: safeParseInt(data.companyId),
        carrierConnectSellerId: safeParseInt(data.sellerId),
        packageDetails: buildPackageDetails(data.packages)
    };
};

// NEW: Inbound Product Pattern - Create Data First, Then Upload Media
/* istanbul ignore next */
export const createCarrierConnectService = (data) => {
    const payload = buildCarrierConnectPayload(data);

    return axios.post(`${API_BASE}/Create`, payload, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

/* istanbul ignore next */
export const updateCarrierConnectMediaService = (formData) => {
    // FormData should be prepared in the component following Inventory module pattern
    // This service now just passes the FormData directly to axios, like inventory module
    return axios.put(`${API_BASE}/update-media`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data'
        }
    });
};

// Helper function to build pagination parameters
const buildPaginationParams = (data) => ({
    pageIndex: data.pageIndex || 1,
    pageSize: data.pageSize || 10
});

// Helper function to build sorting parameters
const buildSortingParams = (data) => ({
    sortColumn: data.sortColumn || 'statusName',
    sortOrder: data.sortOrder === 'ASCENDING' ? 'asc' : 'desc'
});

// Helper function to build filter parameters for request body
const buildBodyFilterParams = (data) => {
    // Helper function to format date to ISO string with time adjustment
    const formatDateToISO = (date, isEndDate = false) => {
        if (!date) return null;
        
        let dateObj;
        if (date instanceof Date) {
            dateObj = new Date(date);
        } else {
            dateObj = new Date(date);
            if (isNaN(dateObj)) return date;
        }
        
        // Create a new date object using UTC to avoid timezone issues
        const utcDate = new Date(Date.UTC(
            dateObj.getFullYear(),
            dateObj.getMonth(),
            dateObj.getDate()
        ));
        
        if (isEndDate) {
            // Set to end of day: 23:59:59.999 UTC
            utcDate.setUTCHours(23, 59, 59, 999);
        } else {
            // Set to start of day: 00:00:00.000 UTC
            utcDate.setUTCHours(0, 0, 0, 0);
        }
        
        return utcDate.toISOString();
    };

    const fromDate = data.fromDate;
    const toDate = data.toDate;

    return {
        carrierConnectNumber: data.carrierConnectNumber || '',
        trackingNumber: data.trackingNumber || '',
        fromDate: formatDateToISO(fromDate, false), // Always start of day for fromDate
        toDate: formatDateToISO(toDate, true), // Always end of day for toDate
        modifiedFromDate: formatDateToISO(data.modifiedFromDate, false),
        modifiedToDate: formatDateToISO(data.modifiedToDate, true), // Always end of day for modifiedToDate
        searchKeyword: data.searchKeyword || ''
    };
};

// Helper function to build complete request body
/* istanbul ignore next */
const buildRequestBody = (data) => ({
    ...buildPaginationParams(data),
    ...buildSortingParams(data),
    ...buildBodyFilterParams(data),
    // Additional parameters from component
    companySellerId: data.companySellerId || 0,
    carrierIdList: data.carrierIdList || [],
    companyIdList: data.companyIdList || [],
    sellerIdList: data.sellerIdList || [],
    consignmentStatusIdList: data.consignmentStatusIdList || []
});

// Helper function to get field mapping for carrier connect
/* istanbul ignore next */
const getFieldMapping = () => ({
    carrierConnectId: 'CarrierConnectNumber',
    carrierStatus: 'StatusId', 
    carrierId: 'CarrierId',
    trackingNumber: 'TrackingNumber',
    subTrackingNumber: 'SubTrackingNumber',
    length: 'Length',
    width: 'Width', 
    height: 'Height',
    weight: 'Weight',
    dimensionUnitId: 'DimensionUnitId',
    weightUnitId: 'WeightUnitId',
    notes: 'Notes'
});

// Helper function to add mapped fields to FormData
/* istanbul ignore next */
const addMappedFieldsToFormData = (formData, data, fieldMapping) => {
    Object.keys(fieldMapping).forEach(formKey => {
        const apiKey = fieldMapping[formKey];
        const value = data[formKey] || '';
        formData.append(apiKey, value);
    });
};

// Helper function to add additional required fields
const addAdditionalRequiredFields = (formData, data) => {
    formData.append('TenantId', data.tenantId || '');
    formData.append('CompanySellerId', data.companySellerId || '');
    formData.append('CreatedBy', data.createdBy || '');
    formData.append('ShippedDate', data.shippedDate || '');
};

// Helper function to filter and get files to upload
const getFilesToUpload = (files) => {
    return files ? files.filter(file => file.file && !file.isDeleted) : [];
};

// Carrier Connect CRUD Services
/* istanbul ignore next */
export const getCarrierConnectListingService = (data) => {
    const requestBody = buildRequestBody(data);

    return axios.post(`${API_BASE}/GetCarrierConnectListing`, requestBody, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
};
/* istanbul ignore next */
export const getCarrierConnectByIdService = (id) => {
    return axios.post(`${API_BASE}/GetById?Id=${id}`, {}, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

// Helper function to add update-specific master images to FormData
const addUpdateMasterImagesToFormData = (formData, data) => {
    if (data.images && data.images.length > 0) {
        const imagesToUpload = getFilesToUpload(data.images);
        appendFilesToUploadData(formData, imagesToUpload.map(img => img.file), 'Images');
    }
};

// Helper function to add update-specific master attachments to FormData
const addUpdateMasterAttachmentsToFormData = (formData, data) => {
    if (data.attachments && data.attachments.length > 0) {
        const attachmentsToUpload = getFilesToUpload(data.attachments);
        appendFilesToUploadData(formData, attachmentsToUpload.map(att => att.file), 'Files');
    }
};

// Helper function to add package data for updates (without files)
const addUpdatePackageData = (formData, data) => {
    if (data.packages && data.packages.length > 0) {
        data.packages.forEach((pkg, index) => {
            Object.keys(pkg).forEach(pkgKey => {
                if (pkgKey !== 'images' && pkgKey !== 'attachments') {
                    formData.append(`packages[${index}].${pkgKey}`, pkg[pkgKey] || '');
                }
            });
        });
    }
};
/* istanbul ignore next */
export const updateCarrierConnectService = (id, data) => {
    const formData = new FormData();
    const fieldMapping = getFieldMapping();
    
    // Add all form data using helper functions
    addMappedFieldsToFormData(formData, data, fieldMapping);
    addAdditionalRequiredFields(formData, data);
    addUpdateMasterImagesToFormData(formData, data);
    addUpdateMasterAttachmentsToFormData(formData, data);
    addUpdatePackageData(formData, data);
    
    return axios.put(`${API_BASE}/UpdateCarrierConnect/${id}`, formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
};

// Helper functions for safe data extraction and parsing
const safeParseInt = (value, defaultValue = 0) => {
    const parsed = parseInt(value);
    return isNaN(parsed) ? defaultValue : parsed;
};

const safeParseFloat = (value, defaultValue = 0.00) => {
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
};

const safeString = (value, defaultValue = '') => {
    return value || defaultValue;
};

const getCarrierConnectNumber = (data) => {
    return safeString(data.carrierConnectId) || safeString(data.carrierConnectNumber);
};

const getStatusId = (data) => {
    const carrierStatus = safeParseInt(data.carrierStatus);
    const statusId = safeParseInt(data.statusId);
    return carrierStatus || statusId;
};

// Helper function to build update carrier connect data payload
const buildUpdateCarrierConnectDataPayload = (data) => {
    return {
        id: safeParseInt(data.id),
        carrierConnectNumber: getCarrierConnectNumber(data),
        carrierId: safeParseInt(data.carrierId),
        statusId: getStatusId(data),
        trackingNumber: safeString(data.trackingNumber),
        subTrackingNumber: safeString(data.subTrackingNumber),
        length: safeParseFloat(data.length),
        width: safeParseFloat(data.width),
        height: safeParseFloat(data.height),
        weight: safeParseFloat(data.weight),
        dimensionUnitId: safeParseInt(data.dimensionUnitId),
        weightUnitId: safeParseInt(data.weightUnitId),
        packageCount: safeParseInt(data.packageCount),
        notes: safeString(data.notes),
        carrierConnectCompanyId: safeParseInt(data.companyId),
        carrierConnectSellerId: safeParseInt(data.sellerId),
        packageActions: buildPackageActions(data.packages || [], data.originalPackages || [], data.id)
    };
};

// NEW: Update Carrier Connect using the new API endpoint structure
export const updateCarrierConnectDataService = (data) => {
    const payload = buildUpdateCarrierConnectDataPayload(data);

    return axios.put(`${API_BASE}/UpdateCarrierConnect`, payload);
};

// Helper function to create package details object for actions
const createPackageDetailsForAction = (pkg, carrierConnectId) => ({
    carrierConnectId: parseInt(carrierConnectId) || 0,
    carrierConnectPackageNumber: pkg.carrierConnectPackageNumber || pkg.trackingNumber || '',
    pkgType: pkg.pkgType || pkg.packageType || '',
    trackingNumber: pkg.trackingNumber || '',
    length: parseFloat(pkg.length) || 0.00,
    width: parseFloat(pkg.width) || 0.00,
    height: parseFloat(pkg.height) || 0.00,
    weight: parseFloat(pkg.weight) || 0.00,
    dimensionUnitId: parseInt(pkg.dimensionUnitId) || 0,
    weightUnitId: parseInt(pkg.weightUnitId) || 0,
    packageCount: parseInt(pkg.packageCount) || 0,
    carrierCompany_Id: parseInt(pkg.carrierCompanyId) || 0,
    service_Id: parseInt(pkg.serviceId) || 0,
    package_Id: parseInt(pkg.packageId) || 0,
    notes: pkg.notes || ''
});

// Helper function to build CREATE actions for new packages
const buildCreateActions = (currentPackages, carrierConnectId) => {
    return currentPackages
        .filter(pkg => !pkg.id || pkg.id <= 0)
        .map(pkg => ({
            action: "CREATE",
            packageDetails: createPackageDetailsForAction(pkg, carrierConnectId)
        }));
};

// Helper function to build UPDATE actions for modified packages
const buildUpdateActions = (currentPackages, originalPackagesMap, carrierConnectId) => {
    const updateActions = [];
    
    currentPackages.forEach(pkg => {
        if (pkg.id && pkg.id > 0) {
            const originalPkg = originalPackagesMap.get(parseInt(pkg.id));
            
            if (originalPkg && hasPackageChanges(pkg, originalPkg)) {
                updateActions.push({
                    action: "UPDATE",
                    packageDetails: {
                        id: parseInt(pkg.id),
                        ...createPackageDetailsForAction(pkg, carrierConnectId)
                    }
                });
            }
        }
    });
    
    return updateActions;
};

// Helper function to build DELETE actions for removed packages
const buildDeleteActions = (originalPackages, processedIds) => {
    return originalPackages
        .filter(originalPkg => 
            originalPkg.id && 
            originalPkg.id > 0 && 
            !processedIds.has(parseInt(originalPkg.id))
        )
        .map(originalPkg => ({
            action: "DELETE",
            packageDetails: {
                id: parseInt(originalPkg.id)
            }
        }));
};

// Helper function to build package actions for update
const buildPackageActions = (currentPackages, originalPackages, carrierConnectId) => {
    // Create a map of original packages by ID for easy lookup
    const originalPackagesMap = new Map();
    originalPackages.forEach(pkg => {
        if (pkg.id && pkg.id > 0) {
            originalPackagesMap.set(parseInt(pkg.id), pkg);
        }
    });

    // Track processed package IDs to identify deletions
    const processedIds = new Set(
        currentPackages
            .filter(pkg => pkg.id && pkg.id > 0)
            .map(pkg => parseInt(pkg.id))
    );

    // Build all actions using focused helper functions
    const createActions = buildCreateActions(currentPackages, carrierConnectId);
    const updateActions = buildUpdateActions(currentPackages, originalPackagesMap, carrierConnectId);
    const deleteActions = buildDeleteActions(originalPackages, processedIds);

    const allActions = [...createActions, ...updateActions, ...deleteActions];

    return allActions;
};

// Helper function to check if a package has changes compared to original
const hasPackageChanges = (currentPkg, originalPkg) => {
    // Compare key fields to detect changes - include both field name variations
    const fieldsToCompare = [
        'carrierConnectPackageNumber', 'trackingNumber', 'pkgType', 'packageType',
        'length', 'width', 'height', 'weight', 'dimensionUnitId', 'weightUnitId',
        'carrierCompanyId', 'serviceTypeId', 'serviceId', 'packageTypeId', 'packageId', 'notes', 'packageCount'
    ];

    const hasChanges = fieldsToCompare.some(field => {
        const currentValue = currentPkg[field] || '';
        const originalValue = originalPkg[field] || '';

        // Convert to string for comparison to handle type differences
        const valuesDiffer = String(currentValue).trim() !== String(originalValue).trim();

        return valuesDiffer;
    });

    return hasChanges;
};

export const deleteCarrierConnectService = (id) => {
    return axios.delete(`${API_BASE}/DeleteCarrierConnect/${id}`);
};

// Connection Testing and Sync Services
export const testCarrierConnectionService = (id) => {
    return axios.post(`${API_BASE}/TestConnection/${id}`);
};

export const syncCarrierConnectionService = (id) => {
    return axios.post(`${API_BASE}/SyncConnection/${id}`);
};

// History Service
export const getCarrierConnectHistoryService = (data) => {
    return axios.post(`${API_BASE}/HistoryByCarrierConnectId`, data, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
};

// Dropdown/Filter Services
export const getAllCarrierConnectCarriersService = () => {
    return axios.get(`${API_BASE}/GetAllCarriers`);
};

export const getAllCarrierConnectTypesService = () => {
    return axios.get(`${API_BASE}/GetAllConnectionTypes`);
};

export const getAllCarrierConnectStatusService = () => {
    return axios.get(`${API_BASE}/GetAllConnectionStatus`);
};

// Bulk Operations
export const bulkUpdateCarrierConnectService = (data) => {
    return axios.post(`${API_BASE}/BulkUpdateConnections`, data);
};

export const exportCarrierConnectService = (data) => {
    return axios.post(`${API_BASE}/ExportConnections`, data, {
        responseType: 'blob'
    });
};

// Configuration Services
export const getCarrierConnectConfigurationService = (carrierId) => {
    return axios.get(`${API_BASE}/GetCarrierConfiguration/${carrierId}`);
};

export const updateCarrierConnectConfigurationService = (carrierId, data) => {
    return axios.put(`${API_BASE}/UpdateCarrierConfiguration/${carrierId}`, data);
};

// Charge Services
export const chargeCarrierConnectService = (data) => {
    return axios.post(`${API_BASE}/DebitCarrierConnectCost`, data, {
        headers: {
            'Content-Type': 'application/json'
        }
    });
};
